import React, {FC} from "react";
import {useAutoRetry} from "@hooks";
import {Skeleton} from "@components/ui/skeleton.tsx";
import {useSpeedDialList} from "@hooks/speed-dial.ts";
import {SetButton} from "@pages/speed-dial/components/set.tsx";
import {ApiSpeedDialSlot} from "@api";
import {Trash2} from "lucide-react";

export function SpeedDialPage() {
    const [slots, loading, error, reload] = useSpeedDialList();
    const MAX_ATTEMPTS = 5;

    const [retryCount, retrying, secondsRemaining, resetRetry] = useAutoRetry({
        shouldRetry: !slots && !!error,
        maxAttempts: MAX_ATTEMPTS,
        retryCallback: reload,
    });

    if (!slots || loading || retrying) {
        return (
            <div className="pt-10 flex flex-col items-center space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                    {Array(6).fill(null).map((_, index) => (
                        <Skeleton key={index} className="h-16 w-full rounded-lg"/>
                    ))}
                </div>
                {retrying && (
                    <p className="text-gray-500 text-md">
                        Failed to load. Trying again ({retryCount + 1}/ {MAX_ATTEMPTS})...
                    </p>
                )}

                {retrying && secondsRemaining > 0 && (
                    <p className="text-gray-500 text-sm">
                        Retrying in {secondsRemaining}s
                    </p>
                )}
            </div>
        );
    }

    if (error && retryCount >= 3) {
        return (
            <div className="pt-32 flex flex-col space-y-6 items-center justify-center text-center">
                <p className="text-red-500">Failed to load speed dials after {MAX_ATTEMPTS} attempts.</p>
                <button
                    onClick={() => {
                        resetRetry();
                        reload();
                    }}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
                >
                    Try again
                </button>
            </div>
        );
    }


    return (
        <div className="py-10 px-6 space-y-12">
            <div className="max-w-5xl mx-auto space-y-8">
                <div className="flex items-center justify-between">
                    <h1 className="text-3xl font-bold text-orange-500 tracking-tight drop-shadow-sm">
                        Speed dial
                    </h1>
                    <SetButton onSet={() => {
                        reload();
                    }}/>
                </div>
                <SlotList slots={slots} reload={reload}/>
            </div>
        </div>
    );
}


const SlotList: FC<{ slots: ApiSpeedDialSlot[], reload: () => void }> = React.memo(({slots, reload}) => {
    return (
        <div className="space-y-6">
            {slots.map((slot) => (
                <Slot key={slot.slot} slot={slot} reload={reload}/>
            ))}
        </div>
    )
});

const Slot: FC<{ slot: ApiSpeedDialSlot; reload: () => void }> = React.memo(({slot, reload}) => {
    const base = slot.pair.base;
    const symbol = `${slot.pair.base.symbol ?? ""}/${slot.pair.quote.symbol ?? ""}`;

    return (
        <div
            className="grid grid-cols-[auto_1fr_auto] items-center gap-4 rounded-xl bg-gradient-to-br from-orange-100/20 via-orange-50/10 to-transparent backdrop-blur-md shadow-xl p-4"
        >
            {base.image ? (
                <img
                    src={base.image}
                    alt={base.symbol}
                    className="h-10 w-10 rounded-full border border-orange-300/40"
                />
            ) : (
                <div
                    className="h-10 w-10 rounded-full bg-orange-300 text-orange-900 font-bold text-sm flex items-center justify-center border border-orange-300/40"
                >
                    {base.symbol?.charAt(0)}
                </div>
            )}

            <div className="flex flex-col self-center">
                <div className="text-sm font-semibold text-orange-200">{symbol}</div>
                <div className="text-xs text-orange-400">Slot: {slot.slot}</div>
            </div>

            <button
                onClick={() => {
                    reload();
                }}
                className="p-2 rounded-lg hover:bg-orange-200/10 transition text-red-800 hover:text-red-500"
                aria-label="Delete"
            >
                <Trash2 className="h-5 w-5"/>
            </button>
        </div>
    );
});